import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

import type { UserDto } from "@/dto";
import { router } from "@/router";
import { RouteNames } from "@/routes";
import { apiGet, apiPost } from "@/services/apiClient";
import { getErrorMessage } from "@/services/utils";

const IS_AUTHENTICATED_LOCAL_STORAGE_KEY = "probabl-is-authenticated";
const EXPIRES_AT_LOCAL_STORAGE_KEY = "probabl-expires-at";

export const useAuthStore = defineStore("auth", () => {
  // State
  const isAuthenticated = ref(localStorage.getItem(IS_AUTHENTICATED_LOCAL_STORAGE_KEY) === "true");
  const expiresAt = ref(
    new Date(localStorage.getItem(EXPIRES_AT_LOCAL_STORAGE_KEY) ?? new Date().toISOString())
  );
  const currentUser = ref<UserDto | null>(null);

  // Getters
  const isExpired = computed(() => {
    return new Date() >= expiresAt.value;
  });

  // Actions
  function _markAuthenticated(expires_at: string) {
    isAuthenticated.value = true;
    expiresAt.value = new Date(expires_at);
    localStorage.setItem(IS_AUTHENTICATED_LOCAL_STORAGE_KEY, "true");
    localStorage.setItem(EXPIRES_AT_LOCAL_STORAGE_KEY, expires_at);
  }

  function _markUnauthenticated() {
    isAuthenticated.value = false;
    currentUser.value = null;
    localStorage.removeItem(IS_AUTHENTICATED_LOCAL_STORAGE_KEY);
    localStorage.removeItem(EXPIRES_AT_LOCAL_STORAGE_KEY);
  }

  async function login(successUri: string, displayRegistration: boolean = false) {
    const uri = encodeURIComponent(successUri);
    const data = await apiGet<{ authorization_url?: string }>(
      `/identity/oauth/login?success_uri=${uri}&display_registration=${displayRegistration}`,
      {
        onError: (error) => {
          console.error(getErrorMessage(error));
          throw error;
        },
      }
    );

    if (data?.authorization_url) {
      window.location.assign(data.authorization_url);
    }
  }

  async function logout() {
    try {
      await apiPost("/identity/oauth/logout", {}, { expectedStatus: 204 });
    } catch (error) {
      console.error(getErrorMessage(error));
    } finally {
      _markUnauthenticated();
    }
  }

  async function refreshToken() {
    type R = { access_token?: string; refresh_token?: string; expires_at?: string };

    const data = await apiPost<R>(
      "/identity/oauth/token/refresh",
      {},
      {
        expectedStatus: 200,
        defaultValue: {} as R,
        onError: (error) => {
          console.error(getErrorMessage(error));
          _markUnauthenticated();
          router.replace({ name: RouteNames.Logout });
        },
      }
    );

    if (data?.access_token && data.refresh_token && data.expires_at) {
      _markAuthenticated(data.expires_at);
    }

    return data ?? ({} as R);
  }

  async function ensureUserIsAuthenticated() {
    const now = new Date();
    if (now >= expiresAt.value && isAuthenticated.value) {
      const { access_token, refresh_token, expires_at } = await refreshToken();
      if (access_token && refresh_token && expires_at) {
        _markAuthenticated(expires_at);
      }
    }
    return isAuthenticated.value;
  }

  async function fetchMe() {
    // Fetch current user
    currentUser.value = (await apiGet<UserDto>("/identity/users/me")) ?? null;
  }

  async function handleLoginSuccess(
    access_token: string,
    refresh_token: string,
    expires_at: string
  ) {
    _markAuthenticated(expires_at);
    await fetchMe();
  }

  return {
    // State
    isAuthenticated,
    expiresAt,
    currentUser,
    // Getters
    isExpired,
    // Actions
    login,
    logout,
    refreshToken,
    ensureUserIsAuthenticated,
    handleLoginSuccess,
    fetchMe,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
